import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gSoftLogoBeyaz from '@/pages/images/g-soft-logo-beyaz.png';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';
import defterKasaLogo from '@/pages/images/defter-kasa-logo.png';
import { useSpecialDayLogo } from '@/hooks/useSpecialDayLogo';

const SOCIAL_LINKS = [
  { icon: 'ri-github-line', label: 'GitHub', href: 'https://github.com/AltusParati' },
  { icon: 'ri-tiktok-line', label: 'TikTok', href: 'https://www.tiktok.com/@gsoftwaree' },
  { icon: 'ri-twitter-x-line', label: 'X', href: 'https://x.com/GSoftwaree' },
  { icon: 'ri-mail-line', label: 'Email', href: 'mailto:info@g-software.org' },
  { icon: 'ri-google-play-line', label: 'Google Play', href: 'https://play.google.com/store/apps/developer?id=G-Software' },
];

export default function SiteFooter() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const brandLogo = useSpecialDayLogo(gSoftLogoBeyaz);

  const NAV_LINKS = [
    { labelKey: 'footer.nav.products', href: '#products', internal: true },
    { labelKey: 'footer.nav.stack',    href: '#stack',    internal: true },
    { labelKey: 'footer.nav.contact',  href: '#contact',  internal: true },
  ];

  const handleInternalLink = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(href.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#0c0c0c] border-t border-white/5 transition-colors duration-300">
      <div className="px-6 lg:px-14 py-14 grid grid-cols-1 md:grid-cols-3 gap-12">

        {/* Col 1 — Brand + about */}
        <div className="md:col-span-1">
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="flex items-center gap-3 group cursor-pointer mb-5"
          >
            <div className="w-10 h-10 border border-white/20 group-hover:border-white transition-colors duration-300 flex items-center justify-center bg-[#141414]">
              <img src={brandLogo} alt="G-Software logo" className="w-9 h-9 object-contain" />
            </div>
            <span className="font-mono text-sm text-white tracking-widest uppercase font-bold">G-Software</span>
          </a>
          <p className="text-neutral-500 text-sm leading-relaxed mb-6 max-w-xs">
            {t('footer.tagline')}
          </p>
          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-9 h-9 border border-white/10 flex items-center justify-center text-neutral-400 hover:border-white/30 hover:text-white transition-all duration-200 cursor-pointer"
              >
                <i className={`${s.icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>

        {/* Col 2 — Navigation */}
        <div>
          <p className="font-mono text-[10px] text-neutral-700 tracking-widest uppercase mb-5">{t('footer.navigation')}</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.labelKey}>
                <a
                  href={l.href}
                  onClick={l.internal ? (e) => handleInternalLink(e, l.href) : undefined}
                  target={!l.internal ? '_blank' : undefined}
                  rel={!l.internal ? 'noopener noreferrer' : undefined}
                  className="flex items-center gap-2 font-mono text-xs text-neutral-400 hover:text-white tracking-wider uppercase transition-colors duration-200 group cursor-pointer"
                >
                  <span className="w-3 h-px bg-neutral-700 group-hover:bg-white transition-colors duration-200" />
                  {t(l.labelKey)}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Products */}
        <div>
          <p className="font-mono text-[10px] text-neutral-700 tracking-widest uppercase mb-5">{t('footer.products')}</p>
          <ul className="space-y-4">
            <li>
              <a
                href="/defter-kasa"
                onClick={(e) => { e.preventDefault(); navigate('/defter-kasa'); }}
                className="group flex items-start gap-3 cursor-pointer"
              >
                <div className="w-8 h-8 border border-white/8 flex items-center justify-center shrink-0 bg-[#161616] group-hover:border-white/20 transition-colors duration-200">
                  <img src={defterKasaLogo} alt="Defter Kasa" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <div className="font-mono text-xs text-neutral-400 group-hover:text-white transition-colors duration-200 mb-0.5">{t('footer.product.defterkasa.name')}</div>
                  <div className="font-mono text-[10px] text-neutral-700 tracking-wider">{t('footer.product.defterkasa.meta')}</div>
                </div>
              </a>
            </li>
            <li>
              <a
                href="/kozmik-gunluk"
                onClick={(e) => { e.preventDefault(); navigate('/kozmik-gunluk'); }}
                className="group flex items-start gap-3 cursor-pointer"
              >
                <div className="w-8 h-8 border border-white/8 flex items-center justify-center shrink-0 bg-[#161616] group-hover:border-white/20 transition-colors duration-200">
                  <img src={gSoftLogoDark} alt={t('footer.product.kozmik.alt')} className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <div className="font-mono text-xs text-neutral-400 group-hover:text-white transition-colors duration-200 mb-0.5">{t('footer.product.kozmik.name')}</div>
                  <div className="font-mono text-[10px] text-neutral-700 tracking-wider">{t('footer.product.kozmik.meta')}</div>
                </div>
              </a>
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-2 border border-white/5 px-3 py-2 w-fit">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-[10px] text-neutral-400 tracking-widest uppercase">{t('footer.systemStatus')}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 px-6 lg:px-14 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <span className="font-mono text-xs text-neutral-800">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </span>
        <div className="flex items-center gap-4">
          <span className="font-mono text-[10px] text-neutral-800 tracking-widest uppercase">{t('footer.values')}</span>
        </div>
      </div>
    </footer>
  );
}

